import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import { ROLE_LABELS } from '../../constants/roles';
import Button from '../../components/ui/Button';

// ─── General Config Card ─────────────────────────────────────────────────────
const GeneralCard = ({ api, t }) => {
  const [cfg, setCfg]       = useState({ site_name: '', registration_open: true, invite_required: false, jwt_expire: '2h', kanban_for_customers: false });
  const [loading, setLoad]  = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const [err, setErr]       = useState('');

  useEffect(() => {
    api.get('/admin/config/general').then(r => { setCfg(r.data); setLoad(false); }).catch(() => setLoad(false));
  }, [api]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      await api.put('/admin/config/general', {
        ...cfg,
        registration_open:    cfg.registration_open    === true || cfg.registration_open    === 'true',
        invite_required:      cfg.invite_required      === true || cfg.invite_required      === 'true',
        kanban_for_customers: cfg.kanban_for_customers === true || cfg.kanban_for_customers === 'true',
      });
      setMsg(t('admin.saved'));
    } catch { setErr(t('admin.saveFailed')); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="admin-card"><p>{t('common.loading')}</p></div>;

  return (
    <div className="admin-card">
      <h2 className="admin-card-title">{t('siteConfig.generalTitle')}</h2>
      {msg && <div className="alert-success">{msg}</div>}
      {err && <div className="alert-error">{err}</div>}
      <form onSubmit={save} className="config-form">
        <div className="config-field">
          <label>{t('siteConfig.siteName')}</label>
          <input value={cfg.site_name || ''} onChange={e => setCfg(p => ({ ...p, site_name: e.target.value }))} maxLength={80} />
        </div>
        <div className="config-field">
          <label>{t('siteConfig.jwtExpire')}</label>
          <select value={cfg.jwt_expire || '2h'} onChange={e => setCfg(p => ({ ...p, jwt_expire: e.target.value }))}>
            {['1h','2h','4h','8h','24h'].map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <span className="config-hint">{t('siteConfig.jwtExpireHint')}</span>
        </div>
        <div className="config-toggles">
          <label className="toggle-row">
            <input type="checkbox"
              checked={cfg.registration_open === true || cfg.registration_open === 'true'}
              onChange={e => setCfg(p => ({ ...p, registration_open: e.target.checked }))}
            />
            <span>{t('siteConfig.registrationOpen')}</span>
          </label>
          <label className="toggle-row">
            <input type="checkbox"
              checked={cfg.invite_required === true || cfg.invite_required === 'true'}
              onChange={e => setCfg(p => ({ ...p, invite_required: e.target.checked }))}
            />
            <span>{t('siteConfig.inviteRequired')}</span>
          </label>
          <label className="toggle-row">
            <input type="checkbox"
              checked={cfg.kanban_for_customers === true || cfg.kanban_for_customers === 'true'}
              onChange={e => setCfg(p => ({ ...p, kanban_for_customers: e.target.checked }))}
            />
            <span>{t('siteConfig.kanbanForCustomers')}</span>
          </label>
        </div>
        <Button type="submit" variant="solid" disabled={saving}>
          {saving ? t('admin.saving') : t('admin.save')}
        </Button>
      </form>
    </div>
  );
};

// ─── SMTP Config Card ────────────────────────────────────────────────────────
const SmtpCard = ({ api, t, userEmail }) => {
  const [cfg, setCfg]         = useState({ smtp_host: '', smtp_port: '465', smtp_user: '', smtp_from_name: '', smtp_password_set: false });
  const [password, setPass]   = useState('');
  const [loading, setLoad]    = useState(true);
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');

  const load = useCallback(() => {
    api.get('/admin/config/smtp').then(r => { setCfg(r.data); setLoad(false); }).catch(() => setLoad(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      const body = { smtp_host: cfg.smtp_host, smtp_port: cfg.smtp_port, smtp_user: cfg.smtp_user, smtp_from_name: cfg.smtp_from_name };
      if (password.trim()) body.smtp_password = password;
      await api.put('/admin/config/smtp', body);
      setMsg(t('admin.saved'));
      setPass('');
      load();
    } catch { setErr(t('admin.saveFailed')); }
    finally { setSaving(false); }
  };

  const testMail = async () => {
    setTesting(true); setMsg(''); setErr('');
    try {
      await api.post('/admin/config/smtp/test');
      setMsg(t('siteConfig.testMailSent', { email: userEmail }));
    } catch (e) {
      setErr(e.response?.data?.error || t('siteConfig.testMailFailed'));
    } finally { setTesting(false); }
  };

  if (loading) return <div className="admin-card"><p>{t('common.loading')}</p></div>;

  return (
    <div className="admin-card">
      <h2 className="admin-card-title">{t('siteConfig.smtpTitle')}</h2>
      {msg && <div className="alert-success">{msg}</div>}
      {err && <div className="alert-error">{err}</div>}
      <form onSubmit={save} className="config-form">
        <div className="config-row">
          <div className="config-field config-field--grow">
            <label>{t('siteConfig.smtpHost')}</label>
            <input value={cfg.smtp_host || ''} onChange={e => setCfg(p => ({ ...p, smtp_host: e.target.value }))} placeholder="mail.example.com" />
          </div>
          <div className="config-field config-field--narrow">
            <label>{t('siteConfig.smtpPort')}</label>
            <input type="number" value={cfg.smtp_port || '465'} onChange={e => setCfg(p => ({ ...p, smtp_port: e.target.value }))} />
          </div>
        </div>
        <div className="config-field">
          <label>{t('siteConfig.smtpUser')}</label>
          <input type="email" value={cfg.smtp_user || ''} onChange={e => setCfg(p => ({ ...p, smtp_user: e.target.value }))} placeholder="noreply@example.com" />
        </div>
        <div className="config-field">
          <label>{t('siteConfig.smtpFromName')}</label>
          <input value={cfg.smtp_from_name || ''} onChange={e => setCfg(p => ({ ...p, smtp_from_name: e.target.value }))} placeholder="Auth System" />
        </div>
        <div className="config-field">
          <label>
            {t('siteConfig.smtpPassword')}
            {cfg.smtp_password_set && <span className="config-badge">{t('siteConfig.passwordSet')}</span>}
          </label>
          <input
            type="password"
            value={password}
            onChange={e => setPass(e.target.value)}
            placeholder={cfg.smtp_password_set ? '••••••••' : t('siteConfig.smtpPasswordPlaceholder')}
            autoComplete="new-password"
          />
          <span className="config-hint">{t('siteConfig.smtpPasswordHint')}</span>
        </div>
        <div className="config-actions">
          <Button type="submit" variant="solid" disabled={saving}>
            {saving ? t('admin.saving') : t('admin.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={testMail} disabled={testing}>
            {testing ? t('siteConfig.testMailSending') : t('siteConfig.testMail')}
          </Button>
        </div>
      </form>
    </div>
  );
};

// ─── Translation Config Card ─────────────────────────────────────────────────
const TranslationCard = ({ api, t }) => {
  const [cfg, setCfg]       = useState({ translation_provider: 'off', claude_model: 'claude-haiku-4-5', deepl_key_set: false, claude_key_set: false });
  const [deeplKey, setDeepl] = useState('');
  const [claudeKey, setClaude] = useState('');
  const [loading, setLoad]  = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const [err, setErr]       = useState('');
  const [usage, setUsage]   = useState(null); // { character_count, character_limit, plan } | 'error' | null

  const load = useCallback(() => {
    api.get('/admin/config/translation').then(r => { setCfg(r.data); setLoad(false); }).catch(() => setLoad(false));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!cfg.deepl_key_set) { setUsage(null); return; }
    api.get('/admin/config/translation/deepl-usage')
      .then(r => setUsage(r.data))
      .catch(() => setUsage('error'));
  }, [api, cfg.deepl_key_set]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      const body = { translation_provider: cfg.translation_provider, claude_model: cfg.claude_model };
      if (deeplKey.trim())  body.deepl_api_key = deeplKey;
      if (claudeKey.trim()) body.claude_api_key = claudeKey;
      await api.put('/admin/config/translation', body);
      setMsg(t('admin.saved')); setDeepl(''); setClaude(''); load();
    } catch { setErr(t('admin.saveFailed')); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="admin-card"><p>{t('common.loading')}</p></div>;

  return (
    <div className="admin-card">
      <h2 className="admin-card-title">{t('siteConfig.translationTitle')}</h2>
      {msg && <div className="alert-success">{msg}</div>}
      {err && <div className="alert-error">{err}</div>}
      <form onSubmit={save} className="config-form">
        <div className="config-field">
          <label>{t('siteConfig.translationProvider')}</label>
          <select value={cfg.translation_provider} onChange={e => setCfg(p => ({ ...p, translation_provider: e.target.value }))}>
            <option value="off">{t('siteConfig.translationOff')}</option>
            <option value="deepl">DeepL</option>
            <option value="claude">Claude API</option>
          </select>
        </div>
        <div className="config-field">
          <label>
            DeepL API Key
            {cfg.deepl_key_set && <span className="config-badge">{t('siteConfig.passwordSet')}</span>}
          </label>
          <input type="password" value={deeplKey} onChange={e => setDeepl(e.target.value)}
            placeholder={cfg.deepl_key_set ? '••••••••' : t('siteConfig.apiKeyPlaceholder')} autoComplete="new-password" />
          {cfg.deepl_key_set && usage === 'error' && (
            <span className="config-hint">{t('siteConfig.deeplUsageError')}</span>
          )}
          {cfg.deepl_key_set && usage && usage !== 'error' && (() => {
            const pct = usage.character_limit > 0
              ? Math.min(100, Math.round((usage.character_count / usage.character_limit) * 100)) : 0;
            const level = pct >= 90 ? ' deepl-usage-fill--danger' : pct >= 75 ? ' deepl-usage-fill--warn' : '';
            return (
              <div className="deepl-usage">
                <div className="deepl-usage-head">
                  <span>{t('siteConfig.deeplUsage')} · {usage.plan === 'free' ? 'Free' : 'Pro'}</span>
                  <span>{usage.character_count.toLocaleString()} / {usage.character_limit.toLocaleString()} ({pct}%)</span>
                </div>
                <div className="deepl-usage-bar">
                  <div className={`deepl-usage-fill${level}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })()}
        </div>
        <div className="config-field">
          <label>
            Claude API Key
            {cfg.claude_key_set && <span className="config-badge">{t('siteConfig.passwordSet')}</span>}
          </label>
          <input type="password" value={claudeKey} onChange={e => setClaude(e.target.value)}
            placeholder={cfg.claude_key_set ? '••••••••' : t('siteConfig.apiKeyPlaceholder')} autoComplete="new-password" />
        </div>
        <div className="config-field">
          <label>{t('siteConfig.claudeModel')}</label>
          <input value={cfg.claude_model || ''} onChange={e => setCfg(p => ({ ...p, claude_model: e.target.value }))} placeholder="claude-haiku-4-5" />
          <span className="config-hint">{t('siteConfig.claudeModelHint')}</span>
        </div>
        <Button type="submit" variant="solid" disabled={saving}>
          {saving ? t('admin.saving') : t('admin.save')}
        </Button>
      </form>
    </div>
  );
};

// ─── Telegram Bot Card ───────────────────────────────────────────────
const TelegramBotCard = ({ api, t }) => {
  const [tg, setTg]           = useState({ telegram_bot_username: '', telegram_enabled: false, telegram_token_set: false });
  const [token, setToken]     = useState('');
  const [loading, setLoad]    = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');
  const [linked, setLinked]         = useState([]);
  const [linkedLoading, setLinkedL] = useState(true);
  const [removing, setRemoving]     = useState(null);

  const load = useCallback(() => {
    api.get('/admin/config/notifications').then(r => {
      setTg(r.data.telegram || {});
      setLoad(false);
    }).catch(() => setLoad(false));
  }, [api]);

  const loadLinked = useCallback(() => {
    api.get('/admin/config/telegram/linked-users')
      .then(r => { setLinked(r.data.users || []); setLinkedL(false); })
      .catch(() => setLinkedL(false));
  }, [api]);

  useEffect(() => { load(); loadLinked(); }, [load, loadLinked]);

  const removeLink = async (u) => {
    if (!window.confirm(t('siteConfig.telegramUnlinkConfirm').replace('{user}', u.username))) return;
    setRemoving(u.id);
    try {
      await api.delete(`/admin/config/telegram/linked-users/${u.id}`);
      loadLinked();
    } catch { /* néma — a lista frissül */ }
    finally { setRemoving(null); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      const body = {
        telegram_bot_username: tg.telegram_bot_username,
        telegram_enabled: tg.telegram_enabled,
      };
      if (token.trim()) body.telegram_bot_token = token;
      await api.put('/admin/config/notifications', body);
      setMsg(t('admin.saved')); setToken(''); load();
    } catch { setErr(t('admin.saveFailed')); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="admin-card"><p>{t('common.loading')}</p></div>;

  return (
    <div className="admin-card">
      <h2 className="admin-card-title">{t('notif.telegramBotSection')}</h2>
      {msg && <div className="alert-success">{msg}</div>}
      {err && <div className="alert-error">{err}</div>}
      <form onSubmit={save} className="config-form">
        <label className="toggle-row">
          <input type="checkbox" checked={!!tg.telegram_enabled}
            onChange={e => setTg(p => ({ ...p, telegram_enabled: e.target.checked }))} />
          {t('siteConfig.telegramEnabled')}
        </label>
        <div className="config-field">
          <label>{t('siteConfig.telegramBotUsername')}</label>
          <input value={tg.telegram_bot_username || ''}
            onChange={e => setTg(p => ({ ...p, telegram_bot_username: e.target.value }))}
            placeholder="VillaPresenceBot" />
        </div>
        <div className="config-field">
          <label>
            {t('siteConfig.telegramBotToken')}
            {tg.telegram_token_set && <span className="config-badge">{t('siteConfig.passwordSet')}</span>}
          </label>
          <input type="password" value={token} onChange={e => setToken(e.target.value)}
            placeholder={tg.telegram_token_set ? '••••••••' : t('siteConfig.apiKeyPlaceholder')} autoComplete="new-password" />
        </div>
        <Button type="submit" variant="solid" disabled={saving}>
          {saving ? t('admin.saving') : t('admin.save')}
        </Button>
      </form>

      <h3 className="config-subtitle">{t('siteConfig.telegramLinkedTitle')}</h3>
      {linkedLoading ? (
        <p className="config-hint">{t('common.loading')}</p>
      ) : linked.length === 0 ? (
        <p className="config-hint">{t('siteConfig.telegramLinkedEmpty')}</p>
      ) : (
        <ul className="linked-users">
          {linked.map(u => (
            <li key={u.id} className="linked-user">
              <div className="linked-user-info">
                <span className="linked-user-name">{u.username}</span>
                <span className="linked-user-meta">{u.email} · {ROLE_LABELS[u.role] ?? `Role ${u.role}`}</span>
              </div>
              <Button variant="danger" size="sm" disabled={removing === u.id} onClick={() => removeLink(u)}>
                {removing === u.id ? '…' : t('siteConfig.telegramUnlinkBtn')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Notification Settings Card (csak maszk-mátrix) ────────────────
const NotificationSettingsCard = ({ api, t }) => {
  const [catalog, setCatalog] = useState([]);
  const [emailEv, setEmailEv] = useState([]);   // engedélyezett email események
  const [tgEv, setTgEv]       = useState([]);   // engedélyezett telegram események
  const [loading, setLoad]    = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');

  const load = useCallback(() => {
    api.get('/admin/config/notifications').then(r => {
      setCatalog(r.data.catalog || []);
      setEmailEv(r.data.mask?.email || []);
      setTgEv(r.data.mask?.telegram || []);
      setLoad(false);
    }).catch(() => setLoad(false));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const isOn = (ch, ev) => (ch === 'email' ? emailEv : tgEv).includes(ev);
  const setOn = (ch, ev, on) => {
    const [list, setList] = ch === 'email' ? [emailEv, setEmailEv] : [tgEv, setTgEv];
    setList(on ? [...new Set([...list, ev])] : list.filter(k => k !== ev));
  };

  // egy kategória összes (esemény,csatorna) cellája
  const cellsOf = (cat) => cat.events.flatMap(e => e.channels.map(ch => ({ ev: e.key, ch })));
  const catAllOn = (cat) => cellsOf(cat).every(c => isOn(c.ch, c.ev));
  const toggleCat = (cat, on) => {
    const cells = cellsOf(cat);
    setEmailEv(prev => {
      let next = new Set(prev);
      cells.filter(c => c.ch === 'email').forEach(c => { on ? next.add(c.ev) : next.delete(c.ev); });
      return [...next];
    });
    setTgEv(prev => {
      let next = new Set(prev);
      cells.filter(c => c.ch === 'telegram').forEach(c => { on ? next.add(c.ev) : next.delete(c.ev); });
      return [...next];
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      const body = {
        email_events: emailEv,
        telegram_events: tgEv,
      };
      await api.put('/admin/config/notifications', body);
      setMsg(t('admin.saved')); load();
    } catch { setErr(t('admin.saveFailed')); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="admin-card"><p>{t('common.loading')}</p></div>;

  return (
    <div className="admin-card">
      <h2 className="admin-card-title">{t('notif.title')}</h2>
      <p className="config-hint">{t('notif.adminHint')}</p>
      {msg && <div className="alert-success">{msg}</div>}
      {err && <div className="alert-error">{err}</div>}
      <form onSubmit={save} className="config-form">
        <div className="notif-matrix">
          <div className="notif-matrix-head">
            <span />
            <span className="notif-col">{t('notif.channel.email')}</span>
            <span className="notif-col">{t('notif.channel.telegram')}</span>
          </div>
          {catalog.map(cat => (
            <div key={cat.key} className="notif-cat">
              <label className="notif-cat-head">
                <input type="checkbox" checked={catAllOn(cat)} onChange={e => toggleCat(cat, e.target.checked)} />
                <strong>{t(`notif.category.${cat.key}`)}</strong>
              </label>
              {cat.events.map(ev => (
                <div key={ev.key} className="notif-row">
                  <span className="notif-ev-label">{t(`notif.event.${ev.key}`)}</span>
                  <span className="notif-cell">
                    {ev.channels.includes('email') && (
                      <input type="checkbox" checked={isOn('email', ev.key)}
                        onChange={e => setOn('email', ev.key, e.target.checked)} />
                    )}
                  </span>
                  <span className="notif-cell">
                    {ev.channels.includes('telegram') && (
                      <input type="checkbox" checked={isOn('telegram', ev.key)}
                        onChange={e => setOn('telegram', ev.key, e.target.checked)} />
                    )}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <Button type="submit" variant="solid" disabled={saving}>
          {saving ? t('admin.saving') : t('admin.save')}
        </Button>
      </form>
    </div>
  );
};

// ─── SSO Config Card ─────────────────────────────────────────────────────────
const SSO_PROVIDERS = [
  { key: 'google',   label: 'Google',   icon: '🔵' },
  { key: 'facebook', label: 'Facebook', icon: '🔷' },
  { key: 'apple',    label: 'Apple ID', icon: '🍎' },
];

const SsoCard = ({ api, t }) => {
  const [config, setConfig]   = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(null);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');
  const [warn, setWarn]       = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/admin/config/sso'); setConfig(r.data); }
    catch { setErr(t('common.error')); }
    finally { setLoading(false); }
  }, [api, t]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (provider, enabled, force = false) => {
    setSaving(provider); setMsg(''); setErr('');
    try {
      await api.put('/admin/config/sso', { provider, enabled, force });
      setConfig(prev => ({ ...prev, [`sso_${provider}_enabled`]: enabled }));
      setMsg(t('admin.ssoSaved'));
    } catch (e) {
      if (e.response?.status === 409) setWarn({ provider, count: e.response.data.count });
      else setErr(t('admin.saveFailed'));
    } finally { setSaving(null); }
  };

  if (loading) return <div className="admin-card"><p>{t('common.loading')}</p></div>;

  return (
    <div className="admin-card">
      <h2 className="admin-card-title">{t('admin.ssoSettings')}</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 13 }}>{t('admin.ssoSettingsDesc')}</p>
      {msg && <div className="alert-success">{msg}</div>}
      {err && <div className="alert-error">{err}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {SSO_PROVIDERS.map(({ key, label, icon }) => {
          const enabled = config[`sso_${key}_enabled`] ?? false;
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'var(--bg-surface-2, var(--bg-surface))', border: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22 }}>{icon}</span>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{enabled ? t('admin.ssoEnabled') : t('admin.ssoDisabled')}</div>
                </div>
              </div>
              <Button variant={enabled ? 'danger' : 'solid'} disabled={saving === key} onClick={() => toggle(key, !enabled)}>
                {saving === key ? t('common.loading') : enabled ? t('admin.disable') : t('admin.enable')}
              </Button>
            </div>
          );
        })}
      </div>
      {warn && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-surface)', padding: 32, maxWidth: 420, width: '90%', border: '1px solid var(--border-color)' }}>
            <h3 style={{ color: 'var(--error-border)', marginBottom: 12 }}>⚠️ {t('admin.ssoWarningTitle')}</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>{t('admin.ssoWarningText').replace('{{count}}', warn.count).replace('{{provider}}', warn.provider)}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setWarn(null)}>{t('admin.cancel')}</Button>
              <Button variant="danger" onClick={() => { setWarn(null); toggle(warn.provider, false, true); }}>{t('admin.forceDisable')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────
const SiteConfigPage = () => {
  const { api, user } = useAuth();
  const { t } = useI18n();

  return (
    <div className="admin-page-content">
      <h1 className="admin-page-title">{t('siteConfig.pageTitle')}</h1>
      <GeneralCard     api={api} t={t} />
      <SmtpCard        api={api} t={t} userEmail={user?.email} />
      <TelegramBotCard api={api} t={t} />
      <TranslationCard api={api} t={t} />
      <NotificationSettingsCard api={api} t={t} />
      <SsoCard         api={api} t={t} />
    </div>
  );
};

export default SiteConfigPage;
